import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import Chip from '../Chip';
import { useLobbyRecords } from '../../hooks/useLobbyRecords';
import type {
  LobbyScope, LobbyGroupBy, LobbyGroupRow, LobbyFlatRow, LobbyGroupOption,
} from '../../types/lobbying';
import LoadingList from '@shared/ui/LoadingList';
import {
  GroupedTableContainer,
  GroupedTableToolbar,
  GroupedTableHeader,
  GroupedTableHeaderCell,
  GroupedTableGroupRow,
  GroupedTableMetricCell,
  GroupedTableRow,
  GroupedTableNotice,
  ChildRowMarker,
  type GroupedTableSort,
} from '../GroupedTable';

interface LobbyingExplorerProps {
  scope: LobbyScope;
  defaultGroupBy?: LobbyGroupBy;
  /** Which group-by options to offer (plus an implicit "None (ledger)"). */
  groupOptions: LobbyGroupOption[];
  /** Optional caveat shown under the table. */
  caveat?: string;
}

// Latest · Group · MetricA · MetricB — static class strings so Tailwind can scan them.
const GROUP_GRID_2 = 'grid-cols-[104px_minmax(0,2.4fr)_110px_110px]';
const GROUP_GRID_1 = 'grid-cols-[104px_minmax(0,2.6fr)_120px]';
// Report · Lobbyist · Client · Bill · Position
const FLAT_GRID = 'grid-cols-[104px_minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,1.2fr)_120px]';

const GROUP_METRICS: Record<Exclude<LobbyGroupBy, 'none'>, { label: string; field: keyof LobbyGroupRow; sortKey: string }[]> = {
  lobbyist: [{ label: 'Bills', field: 'bill_count', sortKey: 'bills' }, { label: 'Clients', field: 'client_count', sortKey: 'clients' }],
  client: [{ label: 'Bills', field: 'bill_count', sortKey: 'bills' }, { label: 'Lobbyists', field: 'lobbyist_count', sortKey: 'lobbyists' }],
  bill: [{ label: 'Lobbyists', field: 'lobbyist_count', sortKey: 'lobbyists' }, { label: 'Clients', field: 'client_count', sortKey: 'clients' }],
  position: [{ label: 'Bills', field: 'bill_count', sortKey: 'bills' }, { label: 'Lobbyists', field: 'lobbyist_count', sortKey: 'lobbyists' }],
};

function fmtMonthYear(d?: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function positionVariant(position?: string | null): 'success' | 'danger' | 'warning' | 'neutral' {
  const p = (position || '').toLowerCase();
  if (p.includes('support')) return 'success';
  if (p.includes('oppos')) return 'danger';
  if (p.includes('amend')) return 'warning';
  return 'neutral';
}

function childFilter(groupBy: LobbyGroupBy, row: LobbyGroupRow): Record<string, string> {
  switch (groupBy) {
    case 'lobbyist': return { lobbyist_id: String(row.group_key) };
    case 'client': return { client_name: row.name };
    case 'bill': return { bill_number: row.name };
    case 'position': return { position: row.name };
    default: return {};
  }
}

const LobbyingExplorer: React.FC<LobbyingExplorerProps> = ({
  scope, defaultGroupBy = 'client', groupOptions, caveat,
}) => {
  const navigate = useNavigate();
  const lobby = useLobbyRecords();
  const { load, setGroupBy, setSort, showAll, fetchRecords } = lobby;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [childrenByKey, setChildrenByKey] = useState<Record<string, LobbyFlatRow[]>>({});
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());

  // (Re)load whenever the pinned scope changes.
  const scopeKey = JSON.stringify(scope);
  useEffect(() => {
    void load(scope, defaultGroupBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Collapse expansions whenever the grouping changes.
  useEffect(() => {
    setExpanded(new Set());
    setChildrenByKey({});
    setLoadingKeys(new Set());
  }, [lobby.groupBy]);

  const fetchChildren = useCallback(async (key: string, filter: Record<string, string>) => {
    setLoadingKeys(prev => new Set(prev).add(key));
    try {
      const list = await fetchRecords(filter);
      setChildrenByKey(prev => ({ ...prev, [key]: list }));
    } catch {
      setChildrenByKey(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingKeys(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [fetchRecords]);

  const toggleExpand = useCallback((key: string, filter: Record<string, string>) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        if (childrenByKey[key] === undefined && !loadingKeys.has(key)) void fetchChildren(key, filter);
      }
      return next;
    });
  }, [childrenByKey, loadingKeys, fetchChildren]);

  const applySort = useCallback((field: string, defaultDir: 'asc' | 'desc') => {
    const dir: 'asc' | 'desc' = lobby.sortBy === field ? (lobby.sortDir === 'asc' ? 'desc' : 'asc') : defaultDir;
    setSort(field, dir);
  }, [lobby.sortBy, lobby.sortDir, setSort]);

  const sort: GroupedTableSort = useMemo(
    () => ({ sortBy: lobby.sortBy, sortDir: lobby.sortDir, onSort: applySort }),
    [lobby.sortBy, lobby.sortDir, applySort],
  );

  const goToBill = (billId?: number | string | null) => {
    if (billId) navigate(`/legislation/${billId}`);
  };

  const groupLabel = useMemo(
    () => groupOptions.find(o => o.key === lobby.groupBy)?.label || 'Group',
    [groupOptions, lobby.groupBy],
  );

  const summaryLine = useMemo(() => {
    const s = lobby.summary;
    if (!s) return null;
    return [
      `${Number(s.unique_bills).toLocaleString()} bills`,
      `${Number(s.unique_clients).toLocaleString()} clients`,
      `${Number(s.unique_lobbyists).toLocaleString()} lobbyists`,
    ].join(' · ');
  }, [lobby.summary]);

  const renderFlatRow = (r: LobbyFlatRow, key: React.Key, isChild = false) => (
    <GroupedTableRow
      key={key}
      grid={FLAT_GRID}
      className={isChild ? 'bg-background-secondary/40 dark:bg-background-dark-tertiary/30' : ''}
    >
      <span className="tabular-nums text-xs text-text-secondary dark:text-text-dark-secondary">{fmtMonthYear(r.report_due_date)}</span>
      <div className="min-w-0 flex items-center gap-1.5">
        {isChild && <ChildRowMarker />}
        <div className="min-w-0">
          <div className="text-[12.5px] text-text-primary dark:text-text-dark-primary truncate">{r.lobbyist_name || '—'}</div>
          {r.lobbyist_firm_name && <div className="text-[10.5px] text-accent-400 truncate">{r.lobbyist_firm_name}</div>}
        </div>
      </div>
      <div className="min-w-0">
        <div className="text-[12.5px] text-text-primary dark:text-text-dark-primary truncate">{r.client_name || '—'}</div>
        {r.client_zip && <div className="text-[10.5px] text-accent-400 truncate">{r.client_zip}</div>}
      </div>
      <div className="min-w-0">
        {r.bill_id ? (
          <button
            type="button"
            onClick={() => goToBill(r.bill_id)}
            className="block max-w-full truncate text-left text-[12.5px] text-primary-700 dark:text-primary-dark-400 hover:underline font-medium"
            title={r.title || r.bill_number}
          >
            {r.bill_number}
          </button>
        ) : (
          <div className="text-[12.5px] text-text-primary dark:text-text-dark-primary truncate font-medium">{r.bill_number}</div>
        )}
        {(r.title || r.session) && (
          <div className="text-[10.5px] text-accent-400 truncate">{[r.session, r.title].filter(Boolean).join(' · ')}</div>
        )}
      </div>
      <span>
        {r.position
          ? <Chip label={r.position} variant={positionVariant(r.position)} size="xs" />
          : <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>}
      </span>
    </GroupedTableRow>
  );

  const renderFlatHeader = () => (
    <GroupedTableHeader grid={FLAT_GRID}>
      <GroupedTableHeaderCell label="Report" sortKey="report_date" defaultDir="desc" sort={sort} />
      <GroupedTableHeaderCell label="Lobbyist" sortKey="lobbyist" defaultDir="asc" sort={sort} />
      <GroupedTableHeaderCell label="Client" sortKey="client" defaultDir="asc" sort={sort} />
      <GroupedTableHeaderCell label="Bill" sortKey="bill" defaultDir="asc" sort={sort} />
      <GroupedTableHeaderCell label="Position" sortKey="position" defaultDir="asc" sort={sort} />
    </GroupedTableHeader>
  );

  const renderGrouped = () => {
    const groupBy = lobby.groupBy as Exclude<LobbyGroupBy, 'none'>;
    const baseMetrics = GROUP_METRICS[groupBy] || GROUP_METRICS.client;
    // The count for the page's own entity is always 1 (a lobbyist page has one
    // lobbyist; a bill page has one bill), so drop that column — e.g. grouping by
    // Bill on a lobbyist page then surfaces Clients (customers) instead.
    const selfField: keyof LobbyGroupRow | null =
      scope.lobbyist_id ? 'lobbyist_count' : scope.bill_number ? 'bill_count' : null;
    const filtered = selfField ? baseMetrics.filter(m => m.field !== selfField) : baseMetrics;
    const metrics = filtered.length ? filtered : baseMetrics;
    const gridClass = metrics.length === 1 ? GROUP_GRID_1 : GROUP_GRID_2;
    const rows = lobby.rows as LobbyGroupRow[];
    return (
      <div>
        <GroupedTableHeader grid={gridClass}>
          <GroupedTableHeaderCell label="Latest" sortKey="latest" defaultDir="desc" sort={sort} />
          <GroupedTableHeaderCell label={groupLabel} sortKey="name" defaultDir="asc" sort={sort} />
          {metrics.map(m => (
            <GroupedTableHeaderCell
              key={m.sortKey}
              label={m.label}
              sortKey={m.sortKey}
              defaultDir="desc"
              alignRight
              sort={sort}
            />
          ))}
        </GroupedTableHeader>
        {rows.map((g, i) => {
          const key = `${g.group_key}-${i}`;
          const isOpen = expanded.has(key);
          const kids = childrenByKey[key];
          return (
            <React.Fragment key={key}>
              <GroupedTableGroupRow
                grid={gridClass}
                open={isOpen}
                onToggle={() => toggleExpand(key, childFilter(groupBy, g))}
                name={g.name}
                sub={g.sub}
                before={
                  <span className="tabular-nums text-xs text-text-secondary dark:text-text-dark-secondary">
                    {fmtMonthYear(g.last_date)}
                  </span>
                }
                after={metrics.map((m, idx) => (
                  <GroupedTableMetricCell
                    key={m.sortKey}
                    primary={idx === 0}
                    value={Number(g[m.field] ?? 0).toLocaleString()}
                  />
                ))}
              />
              {isOpen && (
                loadingKeys.has(key) || kids === undefined ? (
                  <GroupedTableNotice>Loading records…</GroupedTableNotice>
                ) : kids.length === 0 ? (
                  <GroupedTableNotice>No individual records found.</GroupedTableNotice>
                ) : (
                  kids.map((c, ci) => renderFlatRow(c, `${key}-child-${c.id ?? ci}`, true))
                )
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <GroupedTableContainer>
      <GroupedTableToolbar
        value={lobby.groupBy}
        onChange={setGroupBy}
        options={[{ id: 'none' as LobbyGroupBy, label: 'None (ledger)' }, ...groupOptions.map(o => ({ id: o.key, label: o.label }))]}
        summary={summaryLine}
      />

      {/* Body */}
      {lobby.loading && lobby.rows.length === 0 ? (
        <LoadingList surface="plain" rows={6} label="Loading lobbying records" />
      ) : lobby.error ? (
        <div className="px-5 py-10 text-center text-sm text-error-700 dark:text-error-300">{lobby.error}</div>
      ) : lobby.rows.length === 0 ? (
        <div className="px-5 py-12 text-center text-sm text-accent-500 dark:text-text-dark-tertiary">No lobbying activity found.</div>
      ) : lobby.grouped ? (
        renderGrouped()
      ) : (
        <div>
          {renderFlatHeader()}
          {(lobby.rows as LobbyFlatRow[]).map((r, i) => renderFlatRow(r, r.id ?? i))}
        </div>
      )}

      {/* Show all */}
      {lobby.hasMore && (
        <div className="px-5 py-3 border-b border-border-light dark:border-border-dark text-center">
          <button
            type="button"
            onClick={() => showAll()}
            className="text-xs font-semibold text-primary-700 dark:text-primary-dark-400 hover:underline"
          >
            Show all {lobby.total.toLocaleString()} rows
          </button>
        </div>
      )}

      {/* Caveat */}
      <div className="flex items-start gap-[7px] px-5 pt-[11px] pb-3.5 text-accent-500 dark:text-text-dark-tertiary">
        <Info className="w-[13px] h-[13px] flex-none mt-px" />
        <span className="text-[11px] leading-snug">
          {caveat || 'Counts are distinct within each group; a bill can be tracked by multiple lobbyists or clients, so totals overlap across groups. Expand a row to see the individual filings.'}
        </span>
      </div>
    </GroupedTableContainer>
  );
};

export default LobbyingExplorer;
